import React, { useEffect, useState } from 'react';
import {
  Button, Spinner, Table,
} from 'react-bootstrap';
import _ from 'lodash';
import Error from '../../Components/Error';
import useModalState from '../../Hooks/useModalState';
import MessagePresetsService from '../../Services/MessagePresetsService';
import MessagePresetInputModal from './Components/MessagePresetInputModal';
import { TEMPLATE_VARIABLE_MAX } from '../../Utils/passengerTemplate';
import { isEmailChannel, presetChannel } from '../../Utils/messageChannel';

/**
 * Saved Messages — the office's shared library of passenger messages, and
 * the chips in the passenger-message composer.
 *
 * MODELLED ON BOOKING EXTRAS (`Pages/BookingServices`), which is the house
 * shape for a small admin-owned list: a hero with one primary action, a
 * `Table responsive striped bordered hover`, an Edit and a delete button per
 * row, and ONE `useModalState` + input modal doing both the add and the
 * edit. Nothing here is a new pattern, on purpose — the dashboard was
 * reviewed this week for exactly the kind of duplication where two
 * hand-rolled answers to one problem land days apart.
 *
 * WHAT IS DIFFERENT, AND WHY. Booking Extras edits its `sort_order` as a
 * number in its form; here the order IS the chip order a dispatcher reads
 * left to right under real time pressure, so it is arrows on the rows and
 * the list is renumbered 0..n-1 on every move. Two reasons: typing "3" into
 * a form to mean "third" is a translation nobody should have to do, and the
 * presets created before this screen existed all sit on `sort_order: 0`, so
 * swapping two neighbours' numbers would be a no-op on exactly the library
 * that most needs reordering.
 *
 * REMOVAL IS RECOVERABLE. The endpoint's DELETE is soft (`active: false`),
 * so a chip removed by mistake is a one-field fix on the row rather than a
 * restore — the confirm says so instead of threatening permanence it does
 * not deliver.
 *
 * EVERY ROW HAS A CHANNEL, and the two are not interchangeable: a composer
 * offers only the snippets for the channel it is on, so this list shows which
 * is which rather than leaving somebody to wonder why a message they can see
 * here never appears where they need it. The rows that predate the field
 * carry no `channel` and read as WhatsApp, which is what they were written
 * for (`Utils/messageChannel`).
 *
 * AND THE CAP IS THE WHATSAPP TEMPLATE'S. An email row is not measured
 * against it, because there is no template variable to overflow.
 */

/** A body with no variables in it is as long as it will ever be. */
const hasVariables = (body) => /\{[a-z0-9_]+\}/i.test(String(body || ''));

function MessagePresets() {
  const [presets, setPresets] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(false);
  const [error, setError] = useState('');
  const inputModal = useModalState();

  const fetchPresets = async () => {
    setLoading(true);
    try {
      // The UNSCOPED read: bodies exactly as stored, variables and all.
      // The booking-scoped one would hand back somebody else's filled-in
      // pickup address, which is not what an editor should be editing.
      const payload = await MessagePresetsService.fetchPresets();
      setPresets(payload.presets);
      setVariables(payload.variables);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load the saved messages');
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = _.debounce(fetchPresets, 500);

  useEffect(() => {
    debouncedFetch();
    return () => debouncedFetch.cancel();
  }, []);

  const handleDelete = async (preset) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Remove "${preset.label}" from the saved messages?\n\n`
      + 'It stops appearing in the composer for everyone. Nothing already sent is affected, '
      + 'and it can be brought back.',
    )) {
      return;
    }

    try {
      await MessagePresetsService.deletePreset(preset.id);
      fetchPresets();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove the saved message');
    }
  };

  /**
   * Move one preset up or down, and renumber the whole list to match.
   *
   * Every row whose position changed is saved — which for a move of one is
   * two rows, and for a library that was all on `sort_order: 0` is all of
   * them, once. The screen then re-reads rather than trusting its own
   * arithmetic, so what is shown is the order the next composer will see.
   */
  const move = async (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= presets.length || reordering) return;

    const reordered = [...presets];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

    setReordering(true);
    // Shown immediately: the arrows are the one control here that has to
    // feel instant, and the re-read below is what makes it true.
    setPresets(reordered);

    try {
      await Promise.all(
        reordered
          .map((preset, position) => ({ preset, position }))
          .filter(({ preset, position }) => preset.sort_order !== position)
          .map(({ preset, position }) => MessagePresetsService.updatePreset({
            id: preset.id,
            sortOrder: position,
          })),
      );
      await fetchPresets();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save the new order');
      await fetchPresets();
    } finally {
      setReordering(false);
    }
  };

  return (
    <>
      <div className="hero-section mb-4">
        <div className="row align-items-center">
          <div className="col-lg-8 mb-3 mb-lg-0">
            <h1 className="display-5 fw-bold mb-3">
              Saved
              {' '}
              <span className="hero-highlight">Messages</span>
            </h1>
            <p className="lead text-muted mb-4">
              The messages the office can send a passenger in one click, on WhatsApp or by
              email. They appear as buttons in the passenger-message composer — in the
              order below, and only on the channel they were written for.
            </p>
            <div className="hero-actions d-flex gap-2">
              <Button
                onClick={() => inputModal.show({})}
                size="lg"
                variant="primary"
              >
                <i className="fa fa-plus me-2" />
                New Saved Message
              </Button>
            </div>
          </div>
          <div className="col-lg-4 d-none d-lg-block text-end">
            <div className="hero-icon">
              <i className="fa fa-comment-dots fa-5x text-primary opacity-25" />
            </div>
          </div>
        </div>
      </div>

      <Error error={error} setError={setError} />

      {/* The things that go wrong with a SHARED message, said once here and
          again in the form. A preset is sent to every passenger it is ever
          picked for, so a mistake in one is a mistake made many times — and
          this screen has NO PERMISSION GATE, so any dashboard login can
          change what every dispatcher sends. */}
      <div
        className="alert alert-warning d-flex gap-3 align-items-start"
        data-testid="message-presets-warning"
      >
        <i className="fa fa-triangle-exclamation mt-1" />
        <div>
          <strong>Shared by everyone. No greeting, and nothing about one booking.</strong>
          <div className="small mt-1">
            Every dispatcher sees what is saved here, on every booking they pick it for, and
            anyone who can open this screen can change it — so write for
            {' '}
            <em>any</em>
            {' '}
            passenger, not for the one in front of you, and read a change back before you
            leave. WhatsApp already opens its message with
            {' '}
            <em>&quot;Hi Anna, we have a new message for you:&quot;</em>
            {' '}
            — so a WhatsApp message that starts &quot;Hi Anna&quot; greets her twice, and it
            is refused when you save it. Anything that belongs to one booking — a
            driver&apos;s name, a flight number, a price — belongs in the composer, not in
            here. For the parts that change per booking, use a variable: they are filled in
            from the booking at the moment you pick the message.
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="text-muted mt-2">Loading the saved messages...</p>
        </div>
      ) : (
        <Table responsive striped bordered hover>
          <thead>
            <tr>
              <th style={{ width: 110 }}>Order</th>
              <th style={{ width: 120 }}>Channel</th>
              <th style={{ width: 220 }}>Button label</th>
              <th>Message</th>
              <th style={{ width: 150 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {presets.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center text-muted py-4">
                  <i className="fa fa-comment-dots fa-2x mb-2 d-block opacity-50" />
                  No saved messages yet. Create your first one.
                </td>
              </tr>
            ) : (
              presets.map((preset, index) => (
                <tr key={preset.id} data-testid="message-preset-row">
                  <td>
                    <div className="d-flex gap-1 align-items-center">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => move(index, -1)}
                        disabled={reordering || index === 0}
                        title="Move up"
                        aria-label={`Move ${preset.label} up`}
                      >
                        <i className="fa fa-arrow-up" />
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => move(index, 1)}
                        disabled={reordering || index === presets.length - 1}
                        title="Move down"
                        aria-label={`Move ${preset.label} down`}
                      >
                        <i className="fa fa-arrow-down" />
                      </Button>
                    </div>
                  </td>
                  {/* Which composer this one turns up in. Named rather than
                      left to the glyph alone: the row is read at a glance
                      while hunting for the snippet somebody could not find. */}
                  <td data-testid="message-preset-channel">
                    {isEmailChannel(presetChannel(preset)) ? (
                      <span className="badge text-bg-primary">
                        <i className="fa fa-envelope me-1" />
                        Email
                      </span>
                    ) : (
                      <span className="badge text-bg-success">
                        <i className="fab fa-whatsapp me-1" />
                        WhatsApp
                      </span>
                    )}
                  </td>
                  <td><strong>{preset.label}</strong></td>
                  <td>
                    {/* An email subject is half the saved message, so a list
                        that showed only the body would hide it. */}
                    {preset.subject && (
                      <div className="small fw-semibold" data-testid="message-preset-subject">
                        {preset.subject}
                      </div>
                    )}
                    {/* `pre-wrap`: an email body's paragraphs are its own,
                        and running them together here would misrepresent
                        what gets sent. */}
                    <span className="small" style={{ whiteSpace: 'pre-wrap' }}>{preset.body}</span>
                    {hasVariables(preset.body) && (
                      <small className="text-muted d-block mt-1">
                        <i className="fa fa-wand-magic-sparkles me-1" />
                        Filled in from the booking when it is sent.
                      </small>
                    )}
                    {/* The cap belongs to the WhatsApp template; an email
                        body has none to exceed. */}
                    {!isEmailChannel(presetChannel(preset))
                      && !hasVariables(preset.body)
                      && preset.body?.length > TEMPLATE_VARIABLE_MAX && (
                      <small className="text-danger d-block mt-1">
                        Too long to send.
                      </small>
                    )}
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => inputModal.show(preset)}
                      >
                        <i className="fa fa-edit me-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDelete(preset)}
                        aria-label={`Remove ${preset.label}`}
                      >
                        <i className="fa fa-trash" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {inputModal.isVisible && (
        <MessagePresetInputModal
          originalPreset={inputModal.selectedObject}
          variables={variables}
          show={inputModal.isVisible}
          fetchPresets={fetchPresets}
          handleClose={inputModal.hide}
        />
      )}
    </>
  );
}

export default MessagePresets;
